import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Dialog, CircularProgress, InputBase } from '@mui/material';
import { Add as AddIcon, Search as SearchIcon, GroupsOutlined as GroupsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GroupForm } from './GroupForm';
import { GroupListCard, GroupListItem } from './GroupListCard';
import { groupService } from '../../services/group.service';
import { PageHeader } from '../ui/PageHeader';
import { SegmentedControl } from '../ui/SegmentedControl';
import { SurfaceCard } from '../ui/SurfaceCard';
import { EmptyState } from '../ui/EmptyState';

interface GroupsResponse {
  myGroups: GroupListItem[];
  otherGroups: GroupListItem[];
}

type GroupTab = 'mine' | 'discover';

export const GroupList = () => {
  const [myGroups, setMyGroups] = useState<GroupListItem[]>([]);
  const [otherGroups, setOtherGroups] = useState<GroupListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<GroupTab>('mine');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation('groups');

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await groupService.getGroups();
      const groupsData = response.data as GroupsResponse;
      setMyGroups(groupsData.myGroups || []);
      setOtherGroups(groupsData.otherGroups || []);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  const handleSave = async (groupData: { name: string; description: string; private: boolean }) => {
    try {
      await groupService.createGroup({
        name: groupData.name || '',
        description: groupData.description || '',
        private: groupData.private || false
      });
      setOpen(false);
      fetchGroups();
    } catch (error) {
      console.error('Error saving group:', error);
    }
  };

  const visibleGroups = useMemo(() => {
    const source = tab === 'mine' ? myGroups : otherGroups;
    const q = query.trim().toLowerCase();
    if (!q) return source;
    return source.filter(g => g.name.toLowerCase().includes(q) || (g.description || '').toLowerCase().includes(q));
  }, [tab, myGroups, otherGroups, query]);

  const emptyText = query.trim()
    ? t('list.emptySearch')
    : tab === 'mine'
      ? t('list.emptyMine')
      : t('list.emptyDiscover');

  return (
    <>
      <PageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        actions={
          <>
            <Button variant="text" onClick={() => navigate('/contests')}>
              {t('list.allChallenges')}
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              {t('list.createGroup')}
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', justifyContent: 'space-between' }}>
        <SegmentedControl<GroupTab>
          ariaLabel={t('list.filterAria')}
          value={tab}
          onChange={setTab}
          options={[
            { value: 'mine', label: t('list.tabMine', { count: myGroups.length }) },
            { value: 'discover', label: t('list.tabDiscover', { count: otherGroups.length }) }
          ]}
        />
        <Box
          sx={{
            height: 36, flex: 1, maxWidth: 280, minWidth: 180, display: 'flex', alignItems: 'center', gap: 1, px: 1.25,
            border: '1px solid var(--border)', borderRadius: '8px', backgroundColor: 'var(--card)', color: 'var(--muted)'
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          <InputBase
            fullWidth
            placeholder={t('list.searchPlaceholder')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            inputProps={{ 'aria-label': t('list.searchPlaceholder') }}
            sx={{ fontSize: 14, color: 'var(--fg)' }}
          />
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : visibleGroups.length === 0 ? (
        <SurfaceCard bodyPadding={0}>
          <EmptyState icon={<GroupsIcon />} title={t('list.emptyTitle')} description={emptyText} />
        </SurfaceCard>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: '14px' }}>
          {visibleGroups.map(group => (
            <GroupListCard key={group._id} group={group} joined={tab === 'mine'} />
          ))}
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <GroupForm group={null} onSave={handleSave} onCancel={() => setOpen(false)} />
      </Dialog>
    </>
  );
};
